import React from 'react';
import styled from 'styled-components';

interface Props {
  index: number;
  total: number;
  filled: number;
  fieldCount: number;
  children: React.ReactNode;
}

// One visual block of the printed form. Sections are derived from the PDF's own
// vertical whitespace (fillLayout.groupIntoSections) and carry no title — we
// have no title data, and inventing one would mislead. The caption gives the
// inspector the only thing that is actually known: how much is left to do.
const SectionCard = ({ index, total, filled, fieldCount, children }: Props) => {
  const done = fieldCount > 0 && filled === fieldCount;

  return (
    <Card>
      <Head>
        <Eyebrow>{total > 1 ? `Section ${index + 1} of ${total}` : 'Fields'}</Eyebrow>
        {fieldCount > 0 && (
          <Count $done={done}>
            {filled} of {fieldCount} filled
          </Count>
        )}
      </Head>
      <Body>{children}</Body>
    </Card>
  );
};

export default SectionCard;

const Card = styled.section`
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-0);
  box-shadow: var(--shadow-xs);
  padding: 14px 16px 16px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;

  @media only screen and (max-width: 540px) {
    padding: 10px;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 10px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--border);
`;

const Eyebrow = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const Count = styled.span<{ $done: boolean }>`
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid ${({ $done }) => ($done ? 'var(--success-100)' : 'var(--border)')};
  background: ${({ $done }) => ($done ? 'var(--success-50)' : 'var(--neutral-50)')};
  color: ${({ $done }) => ($done ? 'var(--success-700)' : 'var(--neutral-600)')};
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
  width: 100%;
  min-width: 0;

  @media only screen and (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
